// Slot picker — renders hourly (or daily) slots returned by /api/public/spaces/:id/availability.
// User selects START slot; END defaults to next slot but can be extended by clicking subsequent
// available slots. For daily-only spaces (Coworking) renders a single "Dia inteiro" button.

export function SlotPicker({ slots, pricingUnit, startTime, endTime, onChange }) {
  if (!slots || slots.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-6 text-center" data-testid="slots-empty">
        Nenhum horário disponível neste dia. Tente outra data.
      </div>
    );
  }

  if (pricingUnit === "day") {
    const s = slots[0];
    const active = startTime === s.start;
    return (
      <button
        type="button"
        disabled={!s.available}
        onClick={() => s.available && onChange({ start: s.start, end: s.end })}
        data-testid="slot-day"
        className={[
          "w-full rounded-xl border-2 p-4 text-left transition-all active:scale-[0.99]",
          !s.available
            ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
            : active
            ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A1128] shadow-lg shadow-[#D4AF37]/20"
            : "bg-white border-gray-300 hover:border-[#D4AF37] text-[#0A1128]",
        ].join(" ")}
      >
        <div className="text-xs uppercase tracking-wider opacity-70">Reserva</div>
        <div className="text-xl font-bold">Dia inteiro</div>
        <div className="text-xs opacity-70 mt-1">
          {s.available ? "Disponível" : "Indisponível"}
        </div>
      </button>
    );
  }

  // Hourly grid: clicking selects START; clicking another available slot AFTER start extends END.
  const handleClick = (slot) => {
    if (!slot.available) return;
    if (!startTime) {
      onChange({ start: slot.start, end: slot.end });
      return;
    }
    // If clicking same start → reset
    if (slot.start === startTime) {
      onChange({ start: null, end: null });
      return;
    }
    // If clicking AFTER current end → try to extend (ensuring contiguous available slots)
    if (slot.start >= (endTime || startTime)) {
      // Verify contiguity from startTime up to slot.end
      const fromIdx = slots.findIndex((s) => s.start === startTime);
      const toIdx = slots.findIndex((s) => s.start === slot.start);
      if (fromIdx >= 0 && toIdx > fromIdx) {
        const range = slots.slice(fromIdx, toIdx + 1);
        if (range.every((r) => r.available)) {
          onChange({ start: startTime, end: slot.end });
          return;
        }
      }
    }
    // Otherwise → restart selection at this slot
    onChange({ start: slot.start, end: slot.end });
  };

  const isInRange = (slot) => {
    if (!startTime || !endTime) return false;
    return slot.start >= startTime && slot.end <= endTime;
  };

  return (
    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2" data-testid="slots-grid">
      {slots.map((s) => {
        const inRange = isInRange(s);
        const disabled = !s.available;
        return (
          <button
            key={s.start}
            type="button"
            disabled={disabled}
            onClick={() => handleClick(s)}
            data-testid={`slot-${s.label}`}
            className={[
              "rounded-lg border py-3 text-sm font-semibold transition-all active:scale-95 select-none",
              disabled
                ? "bg-gray-100 border-gray-200 text-gray-400 line-through cursor-not-allowed"
                : inRange
                ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A1128]"
                : "bg-white border-gray-300 hover:border-[#D4AF37] text-[#0A1128]",
            ].join(" ")}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}
